import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { PartyFormData, PartyType, PartyStatus, IndividualOrganization } from '../../types/party.types';
import { partyService } from '../../services/party.service';
import { coaService } from '../../services/coa.service';
export function PartyFormPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState<PartyFormData>({
    name: '',
    printAs: '',
    partyType: PartyType.CUSTOMER,
    mobile: '',
    status: PartyStatus.ACTIVE
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [ledgerAccounts, setLedgerAccounts] = useState<Array<{
    value: string;
    label: string;
  }>>([]);
  const [loadingLedgers, setLoadingLedgers] = useState(false);
  useEffect(() => {
    if (isEditMode && id) {
      loadParty(id);
    }
    loadLedgerAccounts();
  }, [id, isEditMode]);
  const loadParty = async (partyId: string) => {
    try {
      const party = await partyService.getPartyById(partyId);
      setFormData({
        name: party.name,
        printAs: party.printAs,
        partyType: party.partyType,
        type: party.type,
        categoryId: party.categoryId,
        email: party.email,
        phone: party.phone,
        mobile: party.mobile,
        vatRegNo: party.vatRegNo,
        bin: party.bin,
        tinNo: party.tinNo,
        nid: party.nid,
        address: party.address,
        bankInfo: party.bankInfo,
        projectIds: party.projects?.map(p => p.projectId),
        associatedLedgerAccountId: party.associatedLedger?.accountCode,
        status: party.status
      });
    } catch (error: any) {
      alert(error.message);
    }
  };
  const loadLedgerAccounts = async () => {
    setLoadingLedgers(true);
    try {
      // Fetch Chart of Accounts that can be used as party ledgers
      // Typically: Accounts Receivable (for Customers), Accounts Payable (for Vendors)
      const response = await coaService.getAccounts({});
      // Filter to show only relevant ledger accounts
      // In real implementation, filter by account type (Assets for AR, Liabilities for AP)
      const ledgerOptions = response.data.map(account => ({
        value: account.accountCode,
        label: `${account.accountCode} - ${account.accountName}`
      }));
      setLedgerAccounts(ledgerOptions);
    } catch (error) {
      console.error('Failed to load ledger accounts:', error);
      // Fallback mock data
      setLedgerAccounts([{
        value: '1300',
        label: '1300 - Accounts Receivable'
      }, {
        value: '1310',
        label: '1310 - Trade Receivables'
      }, {
        value: '2100',
        label: '2100 - Accounts Payable'
      }, {
        value: '2110',
        label: '2110 - Trade Payables'
      }, {
        value: '2120',
        label: '2120 - Vendor Payables'
      }]);
    } finally {
      setLoadingLedgers(false);
    }
  };
  const handleChange = (field: keyof PartyFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
    // Auto-fill Print As with Name if empty
    if (field === 'name' && !formData.printAs) {
      setFormData(prev => ({
        ...prev,
        printAs: value
      }));
    }
  };
  const handleAddressChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };
  const handleBankInfoChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      bankInfo: {
        ...prev.bankInfo,
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };
  const validateForm = async (): Promise<boolean> => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.printAs.trim()) {
      newErrors.printAs = 'Print As is required';
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile is required';
    }
    // Validate uniqueness
    if (formData.mobile) {
      const isUnique = await partyService.validateUniqueness('mobile', formData.mobile, id);
      if (!isUnique) {
        newErrors.mobile = 'Mobile number already exists';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (!isValid) return;
    try {
      if (isEditMode && id) {
        await partyService.updateParty(id, formData);
      } else {
        await partyService.createParty(formData);
      }
      navigate('/parties');
    } catch (error: any) {
      alert(error.message);
    }
  };
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelModal(true);
    } else {
      navigate('/parties');
    }
  };
  const partyTypeOptions = [{
    value: PartyType.CUSTOMER,
    label: 'Customer'
  }, {
    value: PartyType.VENDOR,
    label: 'Vendor'
  }, {
    value: PartyType.OTHER,
    label: 'Other'
  }];
  const typeOptions = [{
    value: '',
    label: 'Select Type'
  }, {
    value: IndividualOrganization.INDIVIDUAL,
    label: 'Individual'
  }, {
    value: IndividualOrganization.ORGANIZATION,
    label: 'Organization'
  }];
  const mockCategories = [{
    value: '',
    label: 'Select Category'
  }, {
    value: 'cat1',
    label: 'Wholesale'
  }, {
    value: 'cat2',
    label: 'Supplier'
  }, {
    value: 'cat3',
    label: 'Retail'
  }];
  const mockCountries = [{
    value: '',
    label: 'Select Country'
  }, {
    value: 'Somalia',
    label: 'Somalia'
  }, {
    value: 'Kenya',
    label: 'Kenya'
  }];
  const mockRegions = [{
    value: '',
    label: 'Select Region'
  }, {
    value: 'Woqooyi Galbeed',
    label: 'Woqooyi Galbeed'
  }, {
    value: 'Banaadir',
    label: 'Banaadir'
  }];
  const mockBanks = [{
    value: '',
    label: 'Select Bank'
  }, {
    value: 'bank1',
    label: 'Premier Bank'
  }, {
    value: 'bank2',
    label: 'Dahabshiil Bank'
  }];
  const mockProjects = [{
    value: 'proj1',
    label: 'Infrastructure Project'
  }, {
    value: 'proj2',
    label: 'Education Initiative'
  }];
  const isOrganization = formData.type === IndividualOrganization.ORGANIZATION;
  const isIndividual = formData.type === IndividualOrganization.INDIVIDUAL;
  const isVendor = formData.partyType === PartyType.VENDOR;
  // Helper text for ledger selection based on party type
  const getLedgerHelperText = () => {
    if (formData.partyType === PartyType.CUSTOMER) {
      return 'Select Accounts Receivable ledger for this customer';
    } else if (formData.partyType === PartyType.VENDOR) {
      return 'Select Accounts Payable ledger for this vendor';
    }
    return 'Select associated ledger account';
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-1187">
      <div className="max-w-5xl mx-auto space-y-6" data-id="element-1188">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-1189">
          <Button variant="ghost" onClick={handleCancel} data-id="element-1190">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1191" />
          </Button>
          <div data-id="element-1192">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1193">
              {isEditMode ? 'Edit Party' : 'Add New Party'}
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1194">
              {isEditMode ? 'Update party information' : 'Create a new party record'}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1195">
          {/* Basic Information */}
          <div className="bg-white rounded-lg border p-6" data-id="element-1196">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1197">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1198">
              <Input label="Name" required value={formData.name} onChange={e => handleChange('name', e.target.value)} error={errors.name} data-id="element-1199" />

              <Input label="Print As" required value={formData.printAs} onChange={e => handleChange('printAs', e.target.value)} error={errors.printAs} helperText="Name to appear on documents" data-id="element-1200" />

              <Select label="Party Type" required options={partyTypeOptions} value={formData.partyType} onChange={e => handleChange('partyType', e.target.value)} data-id="element-1201" />

              <Select label="Type" options={typeOptions} value={formData.type || ''} onChange={e => handleChange('type', e.target.value)} data-id="element-1202" />

              <Select label="Category" options={mockCategories} value={formData.categoryId || ''} onChange={e => handleChange('categoryId', e.target.value)} data-id="element-1203" />

              <Input label="Mobile" required value={formData.mobile} onChange={e => handleChange('mobile', e.target.value)} error={errors.mobile} helperText="Must be unique" data-id="element-1204" />

              <Input label="Email" type="email" value={formData.email || ''} onChange={e => handleChange('email', e.target.value)} data-id="element-1205" />

              <Input label="Phone" value={formData.phone || ''} onChange={e => handleChange('phone', e.target.value)} data-id="element-1206" />

              {isOrganization && <>
                  <Input label="VAT Registration No" value={formData.vatRegNo || ''} onChange={e => handleChange('vatRegNo', e.target.value)} data-id="element-1207" />

                  <Input label="BIN" value={formData.bin || ''} onChange={e => handleChange('bin', e.target.value)} helperText="Business Identification Number" data-id="element-1208" />
                </>}

              {isIndividual && <Input label="NID" value={formData.nid || ''} onChange={e => handleChange('nid', e.target.value)} helperText="National ID" data-id="element-1209" />}

              <Input label="TIN No" value={formData.tinNo || ''} onChange={e => handleChange('tinNo', e.target.value)} helperText="Tax Identification Number" data-id="element-1210" />
            </div>
          </div>

          {/* Ledger Mapping Section */}
          <div className="bg-white rounded-lg border p-6" data-id="element-1211">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1212">
              Ledger Mapping
            </h3>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4" data-id="element-1213">
              <p className="text-sm text-blue-800" data-id="element-1214">
                <strong data-id="element-1215">Important:</strong> Map this party to a Chart of Account
                ledger.
                {formData.partyType === PartyType.CUSTOMER && ' For customers, select an Accounts Receivable ledger.'}
                {formData.partyType === PartyType.VENDOR && ' For vendors, select an Accounts Payable ledger.'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1216">
              <Select label="Associated Ledger Account" options={[{
              value: '',
              label: 'Select Ledger Account'
            }, ...ledgerAccounts]} value={formData.associatedLedgerAccountId || ''} onChange={e => handleChange('associatedLedgerAccountId', e.target.value)} helperText={getLedgerHelperText()} disabled={loadingLedgers} data-id="element-1217" />
              {loadingLedgers && <div className="text-sm text-gray-500 mt-2" data-id="element-1218">
                  Loading ledger accounts...
                </div>}
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-lg border p-6" data-id="element-1219">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1220">
              Address Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1221">
              <Select label="Country" options={mockCountries} value={formData.address?.country || ''} onChange={e => handleAddressChange('country', e.target.value)} data-id="element-1222" />

              <Select label="Region" options={mockRegions} value={formData.address?.region || ''} onChange={e => handleAddressChange('region', e.target.value)} data-id="element-1223" />

              <Input label="District" value={formData.address?.district || ''} onChange={e => handleAddressChange('district', e.target.value)} data-id="element-1224" />

              <div className="md:col-span-2" data-id="element-1225">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1226">
                  Detail Address
                </label>
                <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" rows={2} value={formData.address?.detailAddress || ''} onChange={e => handleAddressChange('detailAddress', e.target.value)} placeholder="Enter complete address" data-id="element-1227" />
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-white rounded-lg border p-6" data-id="element-1228">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1229">
              Account Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1230">
              <Select label="Bank" options={mockBanks} value={formData.bankInfo?.bankId || ''} onChange={e => handleBankInfoChange('bankId', e.target.value)} data-id="element-1231" />

              <Input label="Bank Account Number" value={formData.bankInfo?.accountNumber || ''} onChange={e => handleBankInfoChange('accountNumber', e.target.value)} data-id="element-1232" />
            </div>
          </div>

          {/* Project Information */}
          {isVendor && <div className="bg-white rounded-lg border p-6" data-id="element-1233">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1234">
                Project Information
              </h3>
              <div className="space-y-2" data-id="element-1235">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1236">
                  Associated Projects
                </label>
                <p className="text-xs text-gray-500 mb-2" data-id="element-1237">
                  Select multiple projects for this vendor
                </p>
                {mockProjects.map(project => <label key={project.value} className="flex items-center gap-2" data-id="element-1238">
                    <input type="checkbox" checked={formData.projectIds?.includes(project.value)} onChange={e => {
                const currentIds = formData.projectIds || [];
                const newIds = e.target.checked ? [...currentIds, project.value] : currentIds.filter(id => id !== project.value);
                handleChange('projectIds', newIds);
              }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1239" />
                    <span className="text-sm text-gray-700" data-id="element-1240">
                      {project.label}
                    </span>
                  </label>)}
              </div>
            </div>}

          {/* Actions */}
          <div className="flex justify-end gap-3" data-id="element-1241">
            <Button type="button" variant="secondary" onClick={handleCancel} data-id="element-1242">
              Cancel
            </Button>
            <Button type="submit" variant="primary" data-id="element-1243">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-1244" />
              {isEditMode ? 'Update Party' : 'Create Party'}
            </Button>
          </div>
        </form>
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Unsaved Changes" footer={<>
            <Button variant="secondary" onClick={() => setShowCancelModal(false)} data-id="element-1246">
              Continue Editing
            </Button>
            <Button variant="danger" onClick={() => navigate('/parties')} data-id="element-1247">
              Discard Changes
            </Button>
          </>} data-id="element-1245">
        <p className="text-gray-700" data-id="element-1248">
          You have unsaved changes. Are you sure you want to leave? All changes
          will be lost.
        </p>
      </Modal>
    </div>;
}